import { motion } from "framer-motion";
import { ArrowLeft, Bike, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";
import { ExploreLinks } from "@/components/ExploreLinks";
import { ShareBar } from "@/components/ShareBar";
import { GygTourBand } from "@/components/GetYourGuide";
import { BIKE_TIPS } from "@/data/copenhagen";
import { ShareButton } from "@/components/ShareButton";

const SITE = "https://cphhighlights.com";

const BIKE_APPS = [
  { name: "Voi", url: "https://www.voi.com/da/by/copenhagen" },
  { name: "Dott", url: "https://ridedott.com/da/koer-med-os/" },
  { name: "Bolt", url: "https://bolt.eu/en/cities/copenhagen/" },
  { name: "Donkey Republic", url: "https://www.donkey.bike/da/cities/copenhagen" },
];

// Guided bike tours (GetYourGuide affiliate) — for visitors who'd rather ride with a local.
const BIKE_TOURS = [
  { name: "3h bike ride with a local guide", url: "https://gyg.me/BPeSY0eg" },
  { name: "2.5h bike tour with a Christiania visit", url: "https://gyg.me/S7gE3nHt" },
  { name: "3h architectural biking tour", url: "https://gyg.me/10K1pjJV" },
  { name: "3h local life, away from the tourist spots", url: "https://gyg.me/mIjwyCMF" },
];

const FAQS = [
  {
    q: "Is Copenhagen good for biking?",
    a: "One of the best biking cities on earth — 400 km of protected lanes, dead flat, and locals who commute by bike all year. Just learn the lane rules before you join the rush hour.",
  },
  {
    q: "Do I need to wear a helmet?",
    a: "Helmets aren't legally required in Denmark, and plenty of locals ride without one — but we always recommend wearing one, especially if you're not a confident cyclist. Most rental shops and hotels can lend you a helmet with the bike.",
  },
  {
    q: "How much does it cost to rent a bike in Copenhagen?",
    a: "City-bike apps run from about 49 DKK for 1h, or per-minute pricing plus a small unlock fee. Hotels and hostels often rent bikes for roughly 100–150 DKK a day.",
  },
  {
    q: "Can tourists just bike in Copenhagen?",
    a: "Absolutely — as long as you ride confidently. Practice somewhere quiet first, keep right, signal your turns and stops, and never stop in the lane. Not confident? Walk or take the Metro — no shame in it.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Transport", item: `${SITE}/transport` },
    { "@type": "ListItem", position: 3, name: "Biking in Copenhagen", item: `${SITE}/biking` },
  ],
};

export default function BikingPage() {
  // smooth-scroll with the fixed-header offset (Lenis-aware, like the transport page had)
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.history.replaceState(null, "", `#${id}`);
    if (window.__lenis) {
      window.__lenis.resize();
      window.__lenis.scrollTo(el, { offset: -70 });
    } else {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <main data-testid="biking-page" className="pt-16 md:pt-20 bg-white">
      <Seo
        title="Biking in Copenhagen: Rules, City Bikes & Guided Tours"
        description="How to bike in Copenhagen like a local — the bike-lane rules, city bike apps and prices, and the best guided bike tours."
        path="/biking"
        image="https://images.unsplash.com/photo-1513622470522-26c3c8a854bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative overflow-hidden bg-[#13141A] text-white border-b border-dotted border-white/20">
        <div className="absolute inset-0">
          <img src={"https://images.unsplash.com/photo-1513622470522-26c3c8a854bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-10 pb-16 md:pb-24">
          <div className="flex items-center justify-between mb-10">
            <Link
              to="/transport"
              data-testid="biking-back"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-[#13141A] text-white px-5 py-2.5 rounded-full hover:bg-black transition-colors"
            >
              <ArrowLeft size={16} /> Transport guide
            </Link>
            <ShareButton testid="biking-share" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white">ON TWO WHEELS</p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            Bike Copenhagen<br />
            <span className="font-editorial italic font-600">like a local</span>
          </motion.h1>
          <p className="font-editorial italic font-600 text-xl md:text-2xl text-white/75 mt-4">
            400 km of protected lanes — and a few local rules.
          </p>
          <p className="font-body text-base md:text-lg text-white/85 max-w-2xl mt-6">
            Copenhagen was built for two wheels, and the locals ride like pros. Rent from your hotel
            or unlock a city bike in an app, learn the lane rules below, and you&apos;ll see more of
            the city in a day than most manage in three.
          </p>
          <p data-testid="biking-updated" className="font-body text-xs uppercase tracking-[0.2em] text-white/50 mt-4">
            Last updated: 29 September 2026
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <button
              type="button"
              onClick={() => scrollTo("tours")}
              data-testid="biking-hero-tours-btn"
              className="inline-flex items-center gap-2 font-display font-900 text-base bg-[#DF3226] text-white px-6 py-3.5 rounded-full hover:-translate-y-0.5 hover:shadow-brut transition-all"
            >
              Guided bike tours
            </button>
          </div>
        </div>
      </section>

      {/* rent a bike */}
      <section data-testid="biking-apps" className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-4">CITY BIKES & RENTALS</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
              Rent a bike<br />
              <span className="font-editorial italic font-600">in seconds</span>
            </h2>
            <p className="font-body text-base md:text-lg text-black/70 mt-5 max-w-2xl leading-relaxed">
              Rent from your hotel or hostel, or grab a city bike through an app — unlock one anywhere
              and drop it at any dock.
            </p>
          </Reveal>
          <Reveal delay={0.05} y={30}>
            <div className="mt-7 flex flex-wrap items-center gap-2.5" data-testid="biking-app-list">
              <span className="font-body font-800 text-xs tracking-[0.2em] text-black/50">BIKE APPS:</span>
              {BIKE_APPS.map((a) => (
                <a
                  key={a.name}
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`biking-app-${a.name.split(" ")[0].toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 font-body font-800 text-sm bg-white border border-black/15 px-3.5 py-1.5 hover:bg-black hover:text-white transition-colors"
                >
                  <Bike size={14} /> {a.name}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* the rules */}
      <section id="rules" data-testid="biking-rules" className="bg-[#13141A] text-white border-t border-dotted border-white/20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-4">BIKING RULES</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
              Follow the rules<br />
              <span className="font-editorial italic font-600">of the bike lane</span>
            </h2>
            <p className="font-body text-base md:text-lg text-white/80 mt-5 max-w-2xl leading-relaxed">
              Biking is serious business here — the bike lanes are commuter highways for locals on
              their way to work, not slow sightseeing paths.
            </p>
          </Reveal>
          <Reveal delay={0.1} y={40}>
            <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              {BIKE_TIPS.map((tip, i) => (
                <li key={tip} data-testid="biking-tip" className="flex gap-3 items-start">
                  <Check size={18} className="text-[#DF3226] shrink-0 mt-1" />
                  <span className={`font-body text-base md:text-lg leading-relaxed ${i === BIKE_TIPS.length - 1 ? "font-800 text-white" : "text-white/80"}`}>{tip}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15} y={30}>
            <div data-testid="biking-rule-one" className="mt-10 bg-[#FCEFC5] text-black border border-black shadow-brut rounded-2xl p-6 md:p-8 flex items-start gap-4 max-w-3xl">
              <span className="font-display font-900 text-[#DF3226] text-2xl shrink-0">#1</span>
              <div>
                <p className="font-display font-900 tracking-tight text-xl md:text-2xl leading-tight">
                  Never walk, stand, or take photos in the bike lanes.
                </p>
                <p className="font-body text-sm md:text-base text-black/70 mt-2 leading-relaxed">
                  Step into one and you&apos;ll hear about it — rightly so. This goes for everyone,
                  cyclist or not.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* guided tours */}
      <section id="tours" data-testid="biking-tours" className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-4">RATHER RIDE WITH A LOCAL?</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
              Guided bike tours<br />
              <span className="font-editorial italic font-600">worth booking</span>
            </h2>
            <p className="font-body text-base md:text-lg text-black/70 mt-5 max-w-2xl leading-relaxed">
              If you&apos;d rather take a guided bike tour than go solo, these are the ones worth
              your money:
            </p>
          </Reveal>
          <Reveal delay={0.05} y={30}>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {BIKE_TOURS.map((t, i) => (
                <a
                  key={t.name}
                  href={t.url}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  data-testid={`biking-tour-${i}`}
                  className="inline-flex items-center gap-1.5 font-body font-800 text-sm bg-white border border-black/15 px-3.5 py-1.5 hover:bg-[#DF3226] hover:text-white hover:border-[#DF3226] transition-colors"
                >
                  <Bike size={14} /> {t.name}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            You ride like a local now<br />
            <span className="font-editorial italic font-600 text-white">Plan where to ride</span>
          </h2>
          <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0">
            <Link
              to="/planner"
              data-testid="biking-to-planner"
              className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
            >
              Open the planner
            </Link>
            <Link
              to="/transport"
              data-testid="biking-to-transport"
              className="inline-flex items-center gap-1.5 font-body font-700 text-sm text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft size={15} /> Transport guide
            </Link>
          </div>
        </div>
      </section>

      {/* the one tour to book — end-of-page ad */}
      <GygTourBand tourId="920682" label="THE ONE TOUR TO BOOK" />

      <ExploreLinks
        testid="explore-biking"
        links={[
          { label: "Metro, trains & tickets — the transport guide", to: "/transport" },
          { label: "Ready-made days — the 3-day itinerary", to: "/copenhagen-itinerary" },
          { label: "What else to do — the big things-to-do guide", to: "/things-to-do" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section data-testid="biking-faq" className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Reveal>
            <h2 className="font-display font-900 tracking-tighter text-3xl md:text-4xl mb-8">
              Biking questions, <span className="font-editorial italic font-600">answered</span>
            </h2>
          </Reveal>
          <div className="divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
            {FAQS.map((f, i) => (
              <details key={f.q} data-testid={`biking-faq-${i}`} className="group py-5">
                <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                  {f.q}
                  <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar />
    </main>
  );
}
